import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { FiDownload, FiExternalLink, FiFile, FiFileText, FiUploadCloud, FiX } from "react-icons/fi";
import { Modal } from "./Modal";
import { client } from "../Clients";
import type { StorageObject } from "../API";
import { getStoragePublicBaseUrl } from "../Helper/Constant";
import { resolveStorageUrl as resolveStorageUrlHelper } from "../Helper/storage";
import { normalizeStorageKey } from "../Helper/storage";
import type { HrefTarget } from "../types/layout";
import { MediaLibrary } from "./MediaLibrary";
import TaxonomyMultiSelect from "./TaxonomyMultiSelect";
import type { IApplicationState } from "../Redux/Reducers";

/** Ce que le formulaire hôte apprend du document choisi, au-delà de son URL : de quoi
 * pré-remplir un titre resté vide. Le titre du média est saisi par l'éditeur à l'envoi,
 * c'est donc la forme lisible ; le nom de fichier ne sert que de repli. */
export type PickedDocumentInfo = {
	title?: string | null;
	fileName?: string | null;
};

type FilePickerInputProps = {
	value?: HrefTarget | null;
	onChange: (next?: HrefTarget | null, picked?: PickedDocumentInfo) => void;
	label?: string;
	helper?: string;
	disabled?: boolean;
	folder?: string;
	className?: string;
};

type UploadFormState = {
	title: string;
	fileName: string;
	folder: string;
	taxonomyIds: string[];
	file: File | null;
};

const DEFAULT_FOLDER = "publications";

const initialUploadState: UploadFormState = {
	title: "",
	fileName: "",
	folder: "",
	taxonomyIds: [],
	file: null
};

const isAbsoluteUrl = (value: string) => /^https?:\/\//i.test(value);

const buildPublicUrl = (value?: string | null): string | null => {
	if (!value) return null;
	const resolved = resolveStorageUrlHelper(value);
	return resolved ? resolved : null;
};

/** Human-readable file size (French units). Returns "-" for unknown/zero. */
const formatBytes = (bytes?: number | null): string => {
	if (!bytes || bytes <= 0) return "-";
	const units = ["o", "Ko", "Mo", "Go", "To"];
	let value = bytes;
	let unit = 0;
	while (value >= 1024 && unit < units.length - 1) {
		value /= 1024;
		unit += 1;
	}
	return `${value.toFixed(value < 10 && unit > 0 ? 1 : 0)} ${units[unit]}`;
};

// Pick a preview shape based on extension. Inline-rendered formats (PDF, images)
// get an embedded viewer; everything else falls back to a file-type chip + the
// download button. The Office Online viewer renders xlsx/docx/pptx from a
// publicly reachable URL : handy for read-only checks in the admin.
const detectKind = (urlOrKey?: string | null) => {
	if (!urlOrKey) return "unknown" as const;
	const lower = urlOrKey.split("?")[0].toLowerCase();
	if (/\.(pdf)$/i.test(lower)) return "pdf" as const;
	if (/\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(lower)) return "image" as const;
	if (/\.(xls|xlsx|csv)$/i.test(lower)) return "spreadsheet" as const;
	if (/\.(doc|docx|ppt|pptx|rtf|txt)$/i.test(lower)) return "document" as const;
	return "other" as const;
};

const normalizeIds = (ids?: Array<string | null>) =>
	(ids ?? []).filter((id): id is string => Boolean(id && id.trim())).map((id) => id.trim()).sort();

const sameIdList = (a?: Array<string | null>, b?: Array<string | null>) => {
	const left = normalizeIds(a);
	const right = normalizeIds(b);
	if (left.length !== right.length) return false;
	return left.every((value, index) => value === right[index]);
};

const buildTaxonomyLabel = (id: string, map: Map<string, string>) => map.get(id) ?? id;

/**
 * Document field editor: intentionally the document twin of ImagePickerInput (kept as two
 * parallel components on purpose : same staged-selection modal flow, different value contracts).
 * This one emits an HrefTarget `{ external: <absolute public URL> }` because document links are
 * consumed as plain hrefs by the public site, whereas the image picker persists storage keys.
 * `currentKey` below reverses that on the way in: when the stored URL points at our storage base
 * it is stripped back to a key, so the already-selected asset can be recognised.
 *
 * Browsing is delegated to the shared MediaLibrary (mode "pick", accept "documents" : local nav,
 * the host page URL is never touched). Selection stays staged in pendingKey/pendingAsset until
 * « Utiliser ce document » : or a second pick of the same tile, which replaces the old
 * double-click shortcut. The taxonomy panel saves immediately via the storage API, independent
 * of Confirm. The inline previews (PDF iframe, image, Office viewer) render outside the modal,
 * against the committed value.
 */
export const FilePickerInput: React.FC<FilePickerInputProps> = ({
	value,
	onChange,
	label,
	helper,
	disabled = false,
	folder = DEFAULT_FOLDER,
	className = ""
}) => {
	const [open, setOpen] = useState(false);
	const [pendingKey, setPendingKey] = useState<string>("");
	const [pendingAsset, setPendingAsset] = useState<StorageObject | null>(null);
	const [uploading, setUploading] = useState(false);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
	const [uploadForm, setUploadForm] = useState<UploadFormState>(initialUploadState);
	// Fait recharger la MediaLibrary embarquée (après un upload réussi depuis ce panneau).
	const [libraryRefreshKey, setLibraryRefreshKey] = useState(0);
	const [taxonomyDraftIds, setTaxonomyDraftIds] = useState<string[]>([]);
	const [taxonomySaving, setTaxonomySaving] = useState(false);
	const [taxonomyMessage, setTaxonomyMessage] = useState<string | null>(null);
	const [taxonomyEditing, setTaxonomyEditing] = useState(false);
	const [dragActive, setDragActive] = useState(false);

	const taxonomies = useSelector((state: IApplicationState) => state.taxonomies.items);
	const taxonomyMap = useMemo(() => {
		const map = new Map<string, string>();
		taxonomies.forEach((taxonomie) => {
			if (taxonomie.id) {
				map.set(taxonomie.id, taxonomie.title ?? taxonomie.id);
			}
		});
		return map;
	}, [taxonomies]);

	const externalUrl = (value && typeof value === "object" && value.external) ? String(value.external).trim() : "";
	const currentKey = useMemo(() => {
		if (!externalUrl) return "";
		if (isAbsoluteUrl(externalUrl)) {
			const base = getStoragePublicBaseUrl().trim().replace(/\/+$/, "");
			if (base && externalUrl.startsWith(base)) {
				return externalUrl.slice(base.length).replace(/^\/+/, "");
			}
		}
		return externalUrl;
	}, [externalUrl]);

	const normalizedValue = useMemo(() => normalizeStorageKey(currentKey), [currentKey]);

	const displayValue = useMemo(() => {
		if (!normalizedValue) return "Aucun document sélectionné";
		const name = normalizedValue.split("/").pop() ?? normalizedValue;
		return name;
	}, [normalizedValue]);

	// When the field pins a subfolder (folder="publications/rapports"), uploads land there by
	// default so the freshly uploaded document shows up in the very folder the picker lists.
	const defaultUploadSubfolder = useMemo(() => {
		if (!folder.startsWith("publications/")) return "";
		return folder.slice("publications/".length).replace(/^\/+|\/+$/g, "");
	}, [folder]);

	// Opening the modal resets all staging state and seeds the pending selection with the current
	// value, so "open then Confirm" is a no-op rather than a clear.
	useEffect(() => {
		if (!open) return;
		setPendingKey(normalizedValue);
		setPendingAsset(null);
		setUploadForm(initialUploadState);
		setUploadError(null);
		setUploadSuccess(null);
		setTaxonomyDraftIds([]);
		setTaxonomyMessage(null);
		setTaxonomyEditing(false);
	}, [open, normalizedValue]);

	// The committed value is just a key/URL; back-fill the matching StorageObject (by-key resolves
	// any of the three key variants) so the taxonomy panel can show and edit its tags. Absolute
	// URLs are skipped : they don't live in our storage. Never overwrites an explicit selection.
	useEffect(() => {
		if (!open || !pendingKey || pendingAsset) return;
		const normalized = normalizeStorageKey(pendingKey);
		if (!normalized || isAbsoluteUrl(normalized)) return;
		let cancelled = false;
		client.storage
			.apiStorageByKeyGet(normalized)
			.then((response) => {
				if (!cancelled && response.data) setPendingAsset(response.data);
			})
			.catch(() => {
				/* clé introuvable (fichier supprimé) : panneau taxonomies simplement absent */
			});
		return () => {
			cancelled = true;
		};
	}, [open, pendingAsset, pendingKey]);

	useEffect(() => {
		if (!pendingAsset) {
			setTaxonomyDraftIds([]);
			setTaxonomyEditing(false);
			return;
		}
		setTaxonomyDraftIds(normalizeIds(pendingAsset.taxonomyIds));
		setTaxonomyEditing(false);
	}, [pendingAsset]);

	const handleSelectAsset = (asset: StorageObject) => {
		const key = normalizeStorageKey(asset.key);
		setPendingKey(key);
		setPendingAsset(asset);
	};

	// Pick depuis la MediaLibrary : premier clic = mise en attente ; re-cliquer la vignette déjà
	// sélectionnée confirme directement (remplace l'ancien double-clic du navigateur interne).
	const handlePick = (asset: StorageObject) => {
		const key = normalizeStorageKey(asset.key);
		if (!key) return;
		if (key === normalizeStorageKey(pendingKey)) {
			handleConfirm(key);
			return;
		}
		handleSelectAsset(asset);
	};

	const taxonomyDirty = useMemo(
		() => (pendingAsset ? !sameIdList(pendingAsset.taxonomyIds ?? [], taxonomyDraftIds) : false),
		[pendingAsset, taxonomyDraftIds]
	);

	const handleSaveTaxonomies = async () => {
		if (!pendingAsset?.id || !taxonomyDirty) return;
		setTaxonomySaving(true);
		setTaxonomyMessage(null);
		try {
			const response = await client.storage.apiStorageIdTaxonomiesPut(pendingAsset.id, {
				taxonomyIds: taxonomyDraftIds
			});
			const updated = response.data ?? { ...pendingAsset, taxonomyIds: taxonomyDraftIds };
			setPendingAsset(updated);
			setLibraryRefreshKey((token) => token + 1);
			setTaxonomyMessage("Taxonomies mises à jour.");
			setTaxonomyEditing(false);
		} catch (err) {
			console.error(err);
			setTaxonomyMessage("Impossible de mettre à jour les taxonomies.");
		} finally {
			setTaxonomySaving(false);
		}
	};

	const handleUploadInput = (key: keyof UploadFormState, value: string | string[] | File | null) => {
		setUploadForm((prev) => ({
			...prev,
			[key]: value as never
		}));
	};

	// Prefill title/fileName from the picked file, but never overwrite what the user already typed.
	const handleFileChange = (file: File | null) => {
		handleUploadInput("file", file);
		setUploadSuccess(null);
		setUploadError(null);
		if (!file) return;
		if (!uploadForm.fileName) {
			const base = file.name.substring(0, file.name.lastIndexOf(".")) || file.name;
			handleUploadInput("fileName", base);
		}
		if (!uploadForm.title) {
			handleUploadInput("title", file.name.replace(/\.[^/.]+$/, ""));
		}
	};

	const handleDrop = (event: React.DragEvent<HTMLElement>) => {
		event.preventDefault();
		setDragActive(false);
		if (disabled || uploading) return;
		const file = event.dataTransfer?.files?.[0] ?? null;
		if (file) handleFileChange(file);
	};

	const handleUpload = async () => {
		if (!uploadForm.file) return;
		setUploading(true);
		setUploadError(null);
		setUploadSuccess(null);
		try {
			const taxonomyFilter = uploadForm.taxonomyIds.length ? uploadForm.taxonomyIds.join(",") : undefined;
			// No subfolder typed → default to the field's pinned subfolder so the upload lands
			// in the folder currently listed by the picker.
			const uploadFolder = uploadForm.folder.trim() || defaultUploadSubfolder || undefined;
			const response = await client.storage.apiStorageUploadPublicationsPost(
				uploadForm.title.trim(),
				uploadForm.file,
				uploadForm.fileName.trim() || undefined,
				uploadFolder,
				taxonomyFilter
			);
			const created = response.data ?? null;
			setUploadForm(initialUploadState);
			if (created?.key) {
				const key = normalizeStorageKey(created.key);
				setPendingKey(key);
				setPendingAsset(created);
				// « Upload » applique directement le document fraîchement uploadé
				// (sélection + fermeture), sans second clic sur « Utiliser ce document ».
				handleConfirm(key, created);
				return;
			}
			setUploadSuccess("Document uploadé avec succès.");
			setLibraryRefreshKey((token) => token + 1);
		} catch (err: any) {
			console.error(err);
			const apiMessage = err?.response?.data?.message ?? err?.response?.data ?? null;
			setUploadError(apiMessage ? `Échec de l'upload : ${apiMessage}` : "Échec de l'upload. Veuillez réessayer.");
		} finally {
			setUploading(false);
		}
	};

	const handleClear = () => {
		onChange(null);
		setPendingKey("");
		setPendingAsset(null);
		setOpen(false);
	};

	// `overrideAsset` : juste après un envoi, l'état `pendingAsset` n'est pas encore appliqué,
	// le média fraîchement créé est donc passé directement.
	const handleConfirm = (overrideKey?: string, overrideAsset?: StorageObject | null) => {
		const key = (overrideKey ?? pendingKey).trim();
		if (!key) return;
		const url = buildPublicUrl(normalizeStorageKey(key));
		if (url) {
			const asset = overrideAsset ?? pendingAsset;
			onChange({ external: url }, {
				title: asset?.title ?? null,
				fileName: asset?.fileName ?? key.split("/").pop() ?? null
			});
		}
		setOpen(false);
	};

	const hasFile = Boolean(normalizedValue.trim());
	const hasPendingSelection = Boolean(pendingKey.trim());
	const canUpload = Boolean(uploadForm.title.trim()) && Boolean(uploadForm.file);

	// Resolve the publicly reachable URL for the current value : used by every
	// preview branch below (PDF iframe, <img>, download button, Office viewer).
	const publicUrl = useMemo(() => (hasFile ? buildPublicUrl(normalizedValue) : null), [hasFile, normalizedValue]);
	const fileKind = useMemo(() => detectKind(normalizedValue), [normalizedValue]);
	const officeViewerUrl = useMemo(() => {
		// Microsoft's Office Online viewer renders any publicly fetchable xlsx /
		// docx / pptx as a read-only preview. The source URL has to be absolute
		// AND http(s) reachable from the public internet : falls back to "no
		// preview" if our storage isn't public.
		if (!publicUrl) return null;
		if (fileKind !== "spreadsheet" && fileKind !== "document") return null;
		if (!isAbsoluteUrl(publicUrl)) return null;
		return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(publicUrl)}`;
	}, [publicUrl, fileKind]);

	// Preview of the currently PENDING (clicked, not yet confirmed) document : // rendered inside the modal below the upload panel, so the operator can eyeball
	// the file before « Utiliser ce document ». Mirrors the committed-value preview.
	const pendingNormalized = useMemo(() => normalizeStorageKey(pendingKey), [pendingKey]);
	const pendingUrl = useMemo(() => (pendingNormalized ? buildPublicUrl(pendingNormalized) : null), [pendingNormalized]);
	const pendingKind = useMemo(() => detectKind(pendingNormalized), [pendingNormalized]);
	const pendingOfficeUrl = useMemo(() => {
		if (!pendingUrl) return null;
		if (pendingKind !== "spreadsheet" && pendingKind !== "document") return null;
		if (!isAbsoluteUrl(pendingUrl)) return null;
		return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(pendingUrl)}`;
	}, [pendingUrl, pendingKind]);
	const pendingName = pendingAsset?.fileName || pendingAsset?.title || (pendingNormalized.split("/").pop() ?? pendingNormalized);

	return (
		<div className={`space-y-1 ${className}`}>
			{label && <label className="text-xs font-semibold text-slate-600">{label}</label>}
			<div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-[15px] text-slate-800 shadow-sm transition hover:border-slate-300">
				<FiFile className="text-slate-500 shrink-0" />
				<div className="flex-1 min-w-0 truncate">{displayValue}</div>
				{!disabled && (
					<>
						{hasFile && (
							<button
								type="button"
								onClick={handleClear}
								className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:border-slate-300 hover:text-slate-700"
								aria-label="Retirer le document"
							>
								<FiX />
							</button>
						)}
						<button
							type="button"
							onClick={() => setOpen(true)}
							className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800"
						>
							{hasFile ? "Changer" : "Choisir un document"}
						</button>
					</>
				)}
			</div>
			{hasFile && publicUrl && (
				<div className="my-4 mx-2 sm:mx-4 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-2">
					{fileKind === "pdf" && (
						<iframe
							title={`PDF preview ${displayValue}`}
							src={publicUrl + "#view=Fit&zoom=75"}
							className="h-[55vh] min-h-[420px] w-full rounded border border-slate-200 bg-white"
						/>
					)}
					{fileKind === "image" && (
						<a href={publicUrl} target="_blank" rel="noreferrer" className="block">
							<img
								src={publicUrl}
								alt={displayValue}
								className="max-h-72 w-full rounded border border-slate-200 bg-white object-contain"
							/>
						</a>
					)}
					{fileKind === "spreadsheet" && (
						officeViewerUrl ? (
							<iframe
								title={`Spreadsheet preview ${displayValue}`}
								src={officeViewerUrl}
								className="h-72 w-full rounded border border-slate-200 bg-white"
							/>
						) : (
							<div className="flex items-center gap-2 rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
								<FiFileText className="text-slate-500" />
								Aperçu tableur indisponible (le fichier doit être public). Téléchargez pour ouvrir.
							</div>
						)
					)}
					{fileKind === "document" && (
						officeViewerUrl ? (
							<iframe
								title={`Document preview ${displayValue}`}
								src={officeViewerUrl}
								className="h-72 w-full rounded border border-slate-200 bg-white"
							/>
						) : (
							<div className="flex items-center gap-2 rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
								<FiFileText className="text-slate-500" />
								Aperçu document indisponible. Téléchargez pour ouvrir.
							</div>
						)
					)}
					{fileKind === "other" && (
						<div className="flex items-center gap-2 rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
							<FiFile className="text-slate-500" /> Aperçu non disponible pour ce type de fichier.
						</div>
					)}
					<div className="flex flex-wrap items-center gap-2">
						<a
							href={publicUrl}
							download
							target="_blank"
							rel="noreferrer"
							className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
						>
							<FiDownload className="h-3.5 w-3.5" /> Télécharger
						</a>
						<a
							href={publicUrl}
							target="_blank"
							rel="noreferrer"
							className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
						>
							<FiExternalLink className="h-3.5 w-3.5" /> Ouvrir dans un nouvel onglet
						</a>
						<span className="text-[11px] text-slate-500 truncate">{normalizedValue}</span>
					</div>
				</div>
			)}
			{helper && <p className="text-[11px] text-slate-500">{helper}</p>}

			<Modal show={open} onHide={() => setOpen(false)} size="full85" centered>
				<Modal.Header className="bg-slate-900 text-white" closeButton>
					<div className="flex items-center gap-2">
						<FiFile />
						<Modal.Title>Sélectionner un document</Modal.Title>
					</div>
				</Modal.Header>
				<Modal.Body className="bg-slate-50">
					<div className="flex flex-col gap-4">
						<div className="flex min-w-0 items-start gap-4">
						{/* Navigateur partagé : mode pick : navigation locale, l'URL de la page n'est pas touchée. */}
						<div style={{ flex: "1 1 0%", minWidth: 0 }}>
							<MediaLibrary
								mode="pick"
								accept="documents"
								onPick={handlePick}
								refreshKey={libraryRefreshKey}
								initialPath={folder !== DEFAULT_FOLDER ? folder : undefined}
							/>
						</div>
						<div className="space-y-4" style={{ width: 400, flexShrink: 0 }}>
						<div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
							<div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
								<FiUploadCloud /> Upload d'un nouveau document
							</div>
							<label
								onDragOver={(event) => { event.preventDefault(); if (!dragActive) setDragActive(true); }}
								onDragLeave={(event) => { event.preventDefault(); setDragActive(false); }}
								onDrop={handleDrop}
								className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-center transition ${
									dragActive ? "border-slate-900 bg-slate-100" : "border-slate-300 bg-slate-50 hover:border-slate-400 hover:bg-slate-100"
								}`}
							>
								<FiUploadCloud className="h-7 w-7 text-slate-500" />
								<span className="text-sm font-semibold text-slate-700">
									{dragActive ? "Déposez le document ici" : "Glissez un document ici ou cliquez pour parcourir"}
								</span>
								<span className="text-[11px] text-slate-500">PDF, Word, PowerPoint, Excel, CSV, TXT, RTF</span>
								<input
									type="file"
									accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.rtf"
									onChange={(event) => handleFileChange(event.target.files ? event.target.files[0] : null)}
									className="hidden"
								/>
							</label>
							{uploadForm.file && (
								<p className="flex items-center gap-1 truncate text-xs text-slate-600">
									<FiFileText className="shrink-0 text-slate-500" /> {uploadForm.file.name}
								</p>
							)}
							<div className="grid gap-3">
								<div className="space-y-2">
									<label className="text-xs font-semibold text-slate-600">Titre</label>
									<input
										type="text"
										className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
										placeholder="Rapport annuel 2025"
										value={uploadForm.title}
										onChange={(event) => handleUploadInput("title", event.target.value)}
									/>
								</div>
								<div className="space-y-2">
									<label className="text-xs font-semibold text-slate-600">Nom du fichier (optionnel)</label>
									<input
										type="text"
										className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
										value={uploadForm.fileName}
										onChange={(event) => handleUploadInput("fileName", event.target.value)}
									/>
								</div>
								<div className="space-y-2">
									<label className="text-xs font-semibold text-slate-600">Dossier (optionnel)</label>
									<input
										type="text"
										className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
										placeholder={defaultUploadSubfolder || "rapports"}
										value={uploadForm.folder}
										onChange={(event) => handleUploadInput("folder", event.target.value)}
									/>
								</div>
							</div>
							<TaxonomyMultiSelect
								label="Taxonomies"
								options={taxonomies}
								value={uploadForm.taxonomyIds}
								onChange={(next) => handleUploadInput("taxonomyIds", next)}
								placeholder="Attribuer des taxonomies"
								helper="Sélectionnez une ou plusieurs taxonomies pour ce document."
							/>
							{uploadError && <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{uploadError}</p>}
							{uploadSuccess && <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">{uploadSuccess}</p>}
							<button
								type="button"
								disabled={!canUpload || uploading}
								onClick={handleUpload}
								className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
							>
								{uploading ? "Upload…" : "Upload"}
							</button>
						</div>

						{pendingKey && (
						<div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
							<div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
								<FiFile /> Document sélectionné
							</div>
							<div className="rounded-lg border border-slate-200 bg-slate-50 p-2">
								{pendingKind === "pdf" && pendingUrl && (
									<iframe
										title={`Aperçu ${pendingName}`}
										src={pendingUrl + "#view=Fit&zoom=75"}
										className="h-72 w-full rounded border border-slate-200 bg-white"
									/>
								)}
								{pendingKind === "image" && pendingUrl && (
									<a href={pendingUrl} target="_blank" rel="noreferrer" className="block">
										<img src={pendingUrl} alt={pendingName} className="max-h-56 w-full rounded border border-slate-200 bg-white object-contain" />
									</a>
								)}
								{(pendingKind === "spreadsheet" || pendingKind === "document") &&
									(pendingOfficeUrl ? (
										<iframe
											title={`Aperçu ${pendingName}`}
											src={pendingOfficeUrl}
											className="h-72 w-full rounded border border-slate-200 bg-white"
										/>
									) : (
										<div className="flex items-center gap-2 rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
											<FiFileText className="text-slate-500" /> Aperçu indisponible (le fichier doit être public). Téléchargez pour ouvrir.
										</div>
									))}
								{(pendingKind === "other" || pendingKind === "unknown") && (
									<div className="flex items-center gap-2 rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
										<FiFile className="text-slate-500" /> Aperçu non disponible pour ce type de fichier.
									</div>
								)}
							</div>
							{pendingUrl && (
								<div className="flex flex-wrap items-center gap-2">
									<a href={pendingUrl} download target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50">
										<FiDownload className="h-3.5 w-3.5" /> Télécharger
									</a>
									<a href={pendingUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50">
										<FiExternalLink className="h-3.5 w-3.5" /> Ouvrir
									</a>
								</div>
							)}
							<dl className="space-y-1 text-xs">
								<div className="flex justify-between gap-3">
									<dt className="shrink-0 text-slate-400">Nom</dt>
									<dd className="truncate font-medium text-slate-700" title={pendingName}>{pendingName}</dd>
								</div>
								<div className="flex justify-between gap-3">
									<dt className="shrink-0 text-slate-400">Clé</dt>
									<dd className="truncate font-mono text-slate-600" title={pendingNormalized}>{pendingNormalized}</dd>
								</div>
								{pendingAsset?.folder && (
									<div className="flex justify-between gap-3">
										<dt className="shrink-0 text-slate-400">Dossier</dt>
										<dd className="truncate text-slate-600" title={pendingAsset.folder}>{pendingAsset.folder}</dd>
									</div>
								)}
								{pendingAsset?.contentType && (
									<div className="flex justify-between gap-3">
										<dt className="shrink-0 text-slate-400">Type</dt>
										<dd className="truncate text-slate-600" title={pendingAsset.contentType}>{pendingAsset.contentType}</dd>
									</div>
								)}
								<div className="flex justify-between gap-3">
									<dt className="shrink-0 text-slate-400">Taille</dt>
									<dd className="text-slate-600">{formatBytes(pendingAsset?.sizeBytes)}</dd>
								</div>
								{pendingAsset?.createdAt && (
									<div className="flex justify-between gap-3">
										<dt className="shrink-0 text-slate-400">Ajouté le</dt>
										<dd className="text-slate-600">{new Date(pendingAsset.createdAt).toLocaleDateString("fr-FR")}</dd>
									</div>
								)}
							</dl>
							{pendingAsset && (
								<div className="border-t border-slate-100 pt-3">
									<div className="flex items-center justify-between">
										<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Taxonomies</p>
										<button
											type="button"
											onClick={() => {
												if (!taxonomyEditing) {
													setTaxonomyEditing(true);
													return;
												}
												handleSaveTaxonomies();
											}}
											disabled={(taxonomyEditing ? !taxonomyDirty : false) || taxonomySaving || !pendingAsset.id}
											className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
										>
											{taxonomySaving ? "Enregistrement…" : taxonomyEditing ? "Enregistrer" : "Modifier"}
										</button>
									</div>
									<div className="mt-2">
										{taxonomyEditing ? (
											<TaxonomyMultiSelect
												options={taxonomies}
												value={taxonomyDraftIds}
												onChange={setTaxonomyDraftIds}
												placeholder="Attribuer des taxonomies"
												helper="Filtrez et sélectionnez les taxonomies de ce document."
											/>
										) : (
											<div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
												{pendingAsset.taxonomyIds?.length
													? pendingAsset.taxonomyIds.map((id) => buildTaxonomyLabel(id, taxonomyMap)).join(", ")
													: "Aucune taxonomie attribuée."}
											</div>
										)}
									</div>
									{taxonomyMessage && <p className="mt-2 text-xs text-slate-500">{taxonomyMessage}</p>}
								</div>
							)}
						</div>
						)}
						<div className="flex justify-end gap-2 border-t border-slate-200 pt-3">
							<button
								type="button"
								onClick={() => setOpen(false)}
								className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
							>
								Annuler
							</button>
							<button
								type="button"
								onClick={() => handleConfirm()}
								disabled={!hasPendingSelection}
								className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
							>
								Utiliser ce document
							</button>
						</div>
						</div>
						</div>
					</div>
				</Modal.Body>
			</Modal>
		</div>
	);
};

export default FilePickerInput;
